import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { parseInput, cleanItems, detectFormat, type InputFormat } from '../../pairwise-ranking/utils/inputParsers';

interface ItemInputProps {
  items: string[];
  dotsPerParticipant: number;
  onItemsChange: (items: string[]) => void;
  onDotsChange: (dots: number) => void;
  onStart: () => void;
}

export function ItemInput({ items, dotsPerParticipant, onItemsChange, onDotsChange, onStart }: ItemInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [inputFormat, setInputFormat] = useState<InputFormat>('free-text');
  const [useTextarea, setUseTextarea] = useState(false);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !items.includes(trimmed)) {
      const cleaned = cleanItems([...items, trimmed]);
      onItemsChange(cleaned);
      setInputValue('');
    }
  };

  const handlePaste = (text: string) => {
    const detectedFormat = detectFormat(text);
    setInputFormat(detectedFormat);
    
    try {
      const parsed = parseInput(text, detectedFormat);
      const cleaned = cleanItems(parsed);
      onItemsChange(cleaned);
      setInputValue('');
    } catch (error) {
      alert(`Error parsing input: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRemove = (index: number) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !useTextarea) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleTextareaPaste = (e: React.ClipboardEvent) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.includes('\n') || pastedText.includes(',') || pastedText.trim().startsWith('[')) {
      e.preventDefault();
      handlePaste(pastedText);
    }
  };

  const canStart = items.length >= 1 && dotsPerParticipant > 0;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">
          Set Up Dot Voting
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Enter the items you want to prioritize and set how many dots each participant can allocate.
        </p>

        {/* Dots Per Participant Setting */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
            Dots Per Participant
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              min="1"
              max="20"
              value={dotsPerParticipant}
              onChange={(e) => onDotsChange(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
              className="w-24 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm text-[var(--text-sec)]">
              Each participant can allocate {dotsPerParticipant} dot{dotsPerParticipant !== 1 ? 's' : ''} to the items
            </span>
          </div>
        </div>

        {/* Input Format Toggle */}
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setUseTextarea(!useTextarea)}
            className={`px-3 py-1 text-sm  transition-colors ${
              useTextarea
                ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }`}
          >
            {useTextarea ? 'Single Item Mode' : 'Paste List Mode'}
          </button>
        </div>

        {/* Single Item Input */}
        {!useTextarea && (
          <div className="flex gap-2 mb-6">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter an item name..."
              className="flex-1 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleAdd}
              disabled={!inputValue.trim()}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add
            </button>
          </div>
        )}

        {/* Paste Textarea */}
        {useTextarea && (
          <div className="mb-6">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPaste={handleTextareaPaste}
              placeholder="Paste your list here (one per line, comma-separated, or JSON array)..."
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px] font-mono text-sm"
            />
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => handlePaste(inputValue)}
                disabled={!inputValue.trim()}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed text-sm"
              >
                Parse & Add Items
              </button>
              <select
                value={inputFormat}
                onChange={(e) => setInputFormat(e.target.value as InputFormat)}
                className="px-3 py-2 border border-[var(--rule)] text-sm"
              >
                <option value="free-text">Auto-detect</option>
                <option value="free-text">Free-text (newlines)</option>
                <option value="comma-separated">Comma-separated</option>
                <option value="json">JSON array</option>
              </select>
            </div>
          </div>
        )}

        {/* Items List */}
        {items.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-[var(--text-sec)] mb-3">
              Items ({items.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-[var(--surface)] border border-[var(--rule)]"
                >
                  <span className="text-[var(--text-pri)]">{item}</span>
                  <button
                    onClick={() => handleRemove(index)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={onStart}
          disabled={!canStart}
          className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed font-medium text-lg transition-colors"
        >
          {canStart
            ? `Start Voting (${items.length} items, ${dotsPerParticipant} dot${dotsPerParticipant !== 1 ? 's' : ''} per participant)`
            : 'Add at least 1 item and set dots per participant'}
        </button>
      </div>
    </div>
  );
}


